import { useState } from 'react';
import { FiDownload, FiPlus, FiAlertTriangle } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import Icon from '../components/common/Icon';
import { useUI } from '../context/UIContext';

const DATA_CATEGORIES = [
  { id: 'identity',  label: 'Identity Data',     desc: 'Name, PAN, Aadhaar, email, phone', count: 12, retained: '7 years', legal: 'IT Act 2000' },
  { id: 'financial', label: 'Financial Data',    desc: 'Bank details, salary, TDS records', count: 8,  retained: '8 years', legal: 'Income Tax Act' },
  { id: 'employee',  label: 'Employee Records',  desc: 'HR data, contracts, appraisals',   count: 24, retained: '5 years', legal: 'Labour Laws' },
  { id: 'customer',  label: 'Customer Data',     desc: 'Purchase history, preferences',    count: 6,  retained: '3 years', legal: 'DPDP Act 2023' },
  { id: 'director',  label: 'Director KYC',      desc: 'DIN, DSC, address proof',          count: 5,  retained: 'Perpetual', legal: 'Companies Act 2013' },
  { id: 'contract',  label: 'Contracts & NDA',   desc: 'Vendor agreements, NDAs signed',   count: 18, retained: '10 years', legal: 'Contract Act 1872' },
];

const REQUESTS = [
  { id: 1, type: 'Access',     principal: 'Rajesh Kumar',   email: 'rajesh@example.com', submitted: '2025-03-18', status: 'pending',   deadline: '2025-04-17' },
  { id: 2, type: 'Erasure',    principal: 'Priya Singh',    email: 'priya@example.com',  submitted: '2025-03-10', status: 'completed', deadline: '2025-04-09' },
  { id: 3, type: 'Correction', principal: 'Amit Patel',     email: 'amit@example.com',   submitted: '2025-03-22', status: 'inprog',    deadline: '2025-04-21' },
  { id: 4, type: 'Access',     principal: 'Sunita Mehta',   email: 'sunita@example.com', submitted: '2025-03-25', status: 'pending',   deadline: '2025-04-24' },
];

const BREACH_LOG = [
  { id: 1, date: '2025-01-15', type: 'Phishing Attempt',  affected: 0,   severity: 'low',      status: 'Resolved', notified: false },
  { id: 2, date: '2024-11-03', type: 'Unauthorized Access', affected: 3, severity: 'critical', status: 'Resolved', notified: true },
];

const CONSENT_STATS = { total: 847, active: 721, withdrawn: 89, pending: 37 };

const REQ_STATUS = {
  pending:   { label: 'Pending',     bg: 'bg-[#f3ebd7]', text: 'text-[#8a6a1f]' },
  inprog:    { label: 'In Progress', bg: 'bg-[#edeef3]', text: 'text-[#3a4a66]' },
  completed: { label: 'Completed',   bg: 'bg-[#e8efe5]', text: 'text-[#4a6b3f]' },
};

const SEV_STYLE = {
  low:      { bg: 'bg-[#edeef3]', text: 'text-[#3a4a66]' },
  medium:   { bg: 'bg-[#f3ebd7]', text: 'text-[#8a6a1f]' },
  critical: { bg: 'bg-[#f2e9e6]', text: 'text-[#9a564a]' },
};

export default function DPControlsPage() {
  const { showToast } = useUI();
  const [activeTab, setActiveTab] = useState('inventory');

  const handleAction = (action) => {
    showToast(`${action} — feature available in Enterprise plan`, 'info');
  };

  const TABS = [
    { id: 'inventory',  icon: '📦', label: 'Data Inventory' },
    { id: 'requests',   icon: '📨', label: 'Data Principal Requests' },
    { id: 'consent',    icon: '✅', label: 'Consent Records' },
    { id: 'breach',     icon: '🚨', label: 'Breach Log' },
  ];

  return (
    <AppLayout pageTitle="DP Controls">
      <div className="text-[24px] font-bold text-gray-900 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Data Protection Controls
      </div>
      <div className="text-[13px] text-gray-500 mb-6">
        DPDP Act 2023 · Personal data inventory, consent management, subject rights & breach response
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Data Categories Mapped', value: DATA_CATEGORIES.length,  color: 'text-[#145f5f]',  bar: 'bg-[#145f5f]',  sub: 'Across all departments' },
          { label: 'Active Consents',        value: CONSENT_STATS.active,     color: 'text-[#4a6b3f]',  bar: 'bg-[#4a6b3f]',  sub: `${CONSENT_STATS.total} total collected` },
          { label: 'Open Rights Requests',   value: REQUESTS.filter((r) => r.status !== 'completed').length, color: 'text-[#8a6a1f]', bar: 'bg-[#b07a1a]', sub: 'Pending action' },
          { label: 'Breach Incidents',       value: BREACH_LOG.length,        color: 'text-[#9a564a]',  bar: 'bg-[#9a564a]',  sub: 'Last 12 months' },
        ].map((k) => (
          <div key={k.label} className="relative bg-white rounded-xl border border-gray-200 p-5 overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
            <div className={`absolute bottom-0 left-0 right-0 h-[3px] ${k.bar}`} />
            <div className="text-[10px] uppercase tracking-[1.5px] text-gray-400 font-semibold mb-2">{k.label}</div>
            <div className={`text-[36px] font-bold leading-none ${k.color}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{k.value}</div>
            <div className="text-[11px] text-gray-400 mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-[12.5px] font-semibold rounded-t-lg border-b-2 transition-all -mb-px ${
              activeTab === t.id
                ? 'border-teal-600 text-teal-700 bg-teal-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="inline-flex items-center gap-[6px]">
              <Icon name={t.icon} size={13} />
              {t.label}
            </span>
          </button>
        ))}
      </div>

      {/* Data Inventory Tab */}
      {activeTab === 'inventory' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[14px] font-bold text-gray-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Personal Data Inventory Map
            </div>
            <Button variant="primary" size="sm" leftIcon={<FiDownload size={15} />} onClick={() => handleAction('Export Data Map')}>
              Export Data Map
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#f8f5ef] border-b border-gray-200">
                  {['Category', 'Description', 'Fields Mapped', 'Retention Period', 'Legal Basis', 'Action'].map((h) => (
                    <th key={h} className="text-left px-5 py-2.5 text-[10.5px] uppercase tracking-[1px] text-gray-500 font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DATA_CATEGORIES.map((cat) => (
                  <tr key={cat.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                    <td className="px-5 py-3 text-[12.5px] font-semibold text-gray-800">{cat.label}</td>
                    <td className="px-5 py-3 text-[12px] text-gray-500">{cat.desc}</td>
                    <td className="px-5 py-3 text-[12.5px] font-bold text-teal-700">{cat.count} fields</td>
                    <td className="px-5 py-3 text-[12px] text-gray-600">{cat.retained}</td>
                    <td className="px-5 py-3 text-[11px] text-gray-500" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{cat.legal}</td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => handleAction(`Edit ${cat.label}`)}
                        className="text-[11px] text-teal-600 hover:text-teal-800 font-semibold"
                      >
                        Edit →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Data Principal Requests Tab */}
      {activeTab === 'requests' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[14px] font-bold text-gray-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Data Principal Rights Requests (DPDP Sec. 12–14)
            </div>
            <Button variant="primary" size="sm" leftIcon={<FiPlus size={15} />} onClick={() => handleAction('Log New Request')}>
              Log Request
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#f8f5ef] border-b border-gray-200">
                  {['Request Type', 'Data Principal', 'Submitted', 'Deadline (30d)', 'Status', 'Action'].map((h) => (
                    <th key={h} className="text-left px-5 py-2.5 text-[10.5px] uppercase tracking-[1px] text-gray-500 font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {REQUESTS.map((req) => {
                  const s = REQ_STATUS[req.status];
                  return (
                    <tr key={req.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <span className="text-[12px] font-bold text-gray-800">{req.type}</span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="text-[12.5px] font-semibold text-gray-800">{req.principal}</div>
                        <div className="text-[11px] text-gray-400">{req.email}</div>
                      </td>
                      <td className="px-5 py-3 text-[12px] text-gray-600">{req.submitted}</td>
                      <td className="px-5 py-3 text-[12px] text-gray-600">{req.deadline}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${s.bg} ${s.text}`}>{s.label}</span>
                      </td>
                      <td className="px-5 py-3">
                        {req.status !== 'completed' && (
                          <button
                            onClick={() => handleAction(`Process ${req.type} request`)}
                            className="text-[11px] text-teal-600 hover:text-teal-800 font-semibold"
                          >
                            Process →
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[11px] text-gray-400 text-right">
            Under DPDP Act 2023, requests must be addressed within 30 days. Failure: up to ₹50 Cr penalty.
          </div>
        </div>
      )}

      {/* Consent Records Tab */}
      {activeTab === 'consent' && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            {[
              { label: 'Total Consents', value: CONSENT_STATS.total,     color: 'text-gray-700',   bg: 'bg-gray-50' },
              { label: 'Active',         value: CONSENT_STATS.active,     color: 'text-[#4a6b3f]',  bg: 'bg-[#e8efe5]' },
              { label: 'Withdrawn',      value: CONSENT_STATS.withdrawn,  color: 'text-[#9a564a]',  bg: 'bg-[#f2e9e6]' },
              { label: 'Pending Review', value: CONSENT_STATS.pending,    color: 'text-[#8a6a1f]',  bg: 'bg-[#f3ebd7]' },
            ].map((s) => (
              <div key={s.label} className={`rounded-xl border border-gray-200 p-4 text-center ${s.bg}`}>
                <div className={`text-[32px] font-bold ${s.color}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.value}</div>
                <div className="text-[11px] text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
            <div className="text-[14px] font-bold text-gray-700 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Consent Management Controls
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: '📥', label: 'Export Consent Audit Trail', desc: 'Download full CSV of all consent records with timestamps' },
                { icon: '📋', label: 'Consent Notice Templates', desc: 'DPDP-compliant consent notice templates for all use cases' },
                { icon: '🔄', label: 'Bulk Consent Refresh', desc: 'Send renewal requests for consents older than 12 months' },
                { icon: '📊', label: 'Consent Analytics Report', desc: 'Visualise consent rates, withdrawal trends, categories' },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleAction(item.label)}
                  className="text-left p-4 rounded-xl border border-gray-200 hover:border-teal-300 transition-colors"
                >
                  <div className="text-[12.5px] font-bold text-gray-800 mb-1 flex items-center gap-[6px]">
                    <Icon name={item.icon} size={14} style={{ color: '#145f5f' }} />
                    {item.label}
                  </div>
                  <div className="text-[11px] text-gray-500">{item.desc}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Breach Log Tab */}
      {activeTab === 'breach' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[14px] font-bold text-gray-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Data Breach Incident Register
            </div>
            <Button variant="danger" size="sm" leftIcon={<FiAlertTriangle size={15} />} onClick={() => handleAction('Report Breach')}>
              Report New Breach
            </Button>
          </div>

          {BREACH_LOG.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200 text-[13px] text-gray-400">
              No breach incidents recorded. Maintain a clean record.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {BREACH_LOG.map((b) => {
                const sev = SEV_STYLE[b.severity] || SEV_STYLE.medium;
                return (
                  <div key={b.id} className="bg-white rounded-xl border border-gray-200 p-5" style={{ boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[13px] font-bold text-gray-800">{b.type}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sev.bg} ${sev.text}`}>{b.severity}</span>
                          {b.notified && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">DPB Notified</span>
                          )}
                        </div>
                        <div className="text-[12px] text-gray-500">Incident Date: {b.date} · Affected Individuals: {b.affected}</div>
                      </div>
                      <div className="text-right">
                        <span className="text-[11px] font-bold text-[#4a6b3f]">{b.status}</span>
                        {!b.notified && b.severity === 'critical' && (
                          <div className="text-[10px] text-[#9a564a] mt-1 flex items-center justify-end gap-1">
                            <Icon name="warning" size={11} />
                            DPB notification required
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 rounded-xl border border-[#e7d7ad] bg-[#f3ebd7] p-4">
            <div className="text-[12.5px] font-bold text-[#8a6a1f] mb-1 flex items-center gap-1.5">
              <Icon name="warning" size={14} />
              DPDP Breach Notification Obligation
            </div>
            <div className="text-[11.5px] text-[#8a6a1f] leading-relaxed">
              Under Section 9 of the DPDP Act 2023, personal data breaches must be notified to the Data Protection Board (DPB)
              and affected data principals without undue delay. Failure to notify: up to ₹200 Crore penalty.
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
